import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSiteAssets, updateSiteAssets } from '../../../models/assetsModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import Button from '../../../components/common/Button';

function ManageSiteAssets() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const canViewAssets = useHasAnyPermission(['View Assets']);
  const canManageAssets = useHasAnyPermission(['Manage Assets']);
  
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [siteName, setSiteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Use the correct API endpoint for getting site assets data
      const result = await getSiteAssets(siteId);
      

      
      // Set site name from the site object
      setSiteName(result.site?.site_name || 'Unknown Site');
      
      // Set all available assets
      setAssets(result.assets || []);
      
      // Set currently selected assets for this site
      // Now that backend is fixed, use the selected array directly from API response
      setSelectedAssets(result.selected || []);
    } catch (error) {
      console.error('Error fetching site assets:', error);
      toast.error('Failed to load site assets data');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!canViewAssets) {
      toast.error('You do not have permission to view assets');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [fetchData, canViewAssets, navigate]);

  const handleAssetSelection = (selectedOptions) => {
    const selectedIds = Array.from(selectedOptions).map(option => option.value);
    setSelectedAssets(selectedIds);
  };

  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();
    
    if (!canManageAssets) {
      toast.error('You do not have permission to manage assets');
      return;
    }

    setSaving(true);
    try {
      // Prepare the data in the format expected by the backend
      // This should match the form data from the original PHP
      const formData = {
        site_id: parseInt(siteId),
        site_name: siteName,
        site_assets: selectedAssets.join(',')
      };
      
      await updateSiteAssets(formData);
      toast.success('Site assets updated successfully');
      // Refresh the data to show updated assignments
      await fetchData();
    } catch (error) {
      console.error('Error updating site assets:', error);
      toast.error('Failed to update site assets');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-2 py-1">
        <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/assets/onsite')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Sites
        </button>
        <h1 className="text-2xl font-semibold text-gray-800">Update Site Assets</h1>
        <p className="text-gray-600 mt-1">Configure which assets are assigned to this site location</p>
      </div>



      <form onSubmit={handleSaveChanges} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Site Information</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Site Name *
            </label>
            <input
              type="text"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              disabled={!canManageAssets}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 disabled:bg-gray-50"
              placeholder="Enter site name"
              required
            />
          </div>
        </div>

        {/* Asset Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Asset Selection</h2>
            <div className="text-sm text-gray-600">
              {selectedAssets.length} of {assets.length} assets selected
            </div>
          </div>
          
          {/* Dual Pane Asset Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Available Assets */}
            <div className="border border-gray-300 rounded-lg">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Available Assets</h4>
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Click on an asset to add it to the site</p>
              </div>
              <div className="max-h-64 overflow-y-auto p-2">
                {assets
                  .filter(asset => {
                    // Check if asset is already selected
                    const isSelected = selectedAssets.includes(asset.asset_id);
                    
                    // Filter by search term
                    const matchesSearch = (asset.asset_name?.toLowerCase() || '').includes(assetSearch.toLowerCase()) ||
                                        (asset.asset_tag?.toLowerCase() || '').includes(assetSearch.toLowerCase());
                    
                    return !isSelected && matchesSearch;
                  })
                  .map((asset) => (
                    <div
                      key={asset.asset_id}
                      onClick={() => {
                        if (canManageAssets) {
                          setSelectedAssets(prev => [...prev, asset.asset_id]);
                        }
                      }}
                      className={`flex items-center justify-between p-2 rounded-md transition-colors duration-150 border-b border-gray-100 last:border-b-0 ${
                        canManageAssets ? 'hover:bg-green-50 cursor-pointer' : 'cursor-not-allowed opacity-50'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.asset_name}
                        </div>
                        {asset.asset_tag && (
                          <div className="text-xs text-gray-500">
                            {asset.asset_tag}
                          </div>
                        )}
                        {asset.asset_site && asset.asset_site !== parseInt(siteId) && (
                          <div className="text-xs text-amber-600">
                            Currently assigned to site {asset.asset_site}
                          </div>
                        )}
                      </div>
                      {canManageAssets && (
                        <button
                          type="button"
                          className="ml-2 text-green-600 hover:text-green-800 flex-shrink-0"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                {assets.filter(asset => {
                  const isSelected = selectedAssets.includes(asset.asset_id);
                  const matchesSearch = (asset.asset_name?.toLowerCase() || '').includes(assetSearch.toLowerCase()) ||
                                      (asset.asset_tag?.toLowerCase() || '').includes(assetSearch.toLowerCase());
                  return !isSelected && matchesSearch;
                }).length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-sm">No assets found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Assets */}
            <div className="border border-gray-300 rounded-lg">
              <div className="bg-green-50 px-4 py-3 border-b border-gray-300">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">
                    Selected Assets ({selectedAssets.length})
                  </h4>
                  <div className="flex items-center gap-2">
                    {selectedAssets.length > 0 && canManageAssets && (
                      <button
                        type="button"
                        onClick={() => setSelectedAssets([])}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-2">
                {selectedAssets.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-sm">No assets selected</p>
                    <p className="text-xs text-gray-400 mt-1">Click assets from the left to add them</p>
                  </div>
                ) : (
                  selectedAssets.map((assetId) => {
                    const asset = assets.find(a => a.asset_id === assetId);
                    if (!asset) return null;
                    
                    return (
                      <div 
                        key={assetId}
                        className="flex items-center justify-between p-2 bg-white border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{asset.asset_name}</div>
                          {asset.asset_tag && (
                            <div className="text-xs text-gray-500">{asset.asset_tag}</div>
                          )}
                        </div>
                        {canManageAssets && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAssets(prev => prev.filter(id => id !== assetId));
                            }}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {assets.length > 0 && canManageAssets && (
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 mt-4">
              <button
                type="button"
                onClick={() => {
                  const allAssetIds = assets.map(a => a.asset_id);
                  setSelectedAssets(allAssetIds);
                }}
                className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-md hover:bg-green-200 transition-colors"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={() => setSelectedAssets([])}
                className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded-md hover:bg-red-200 transition-colors"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Save Button */}
        {canManageAssets && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default ManageSiteAssets;